'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { CompactTable } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
export const POSITION_MANAGER_COLUMN_ID = 'positionManager';

/** Applied to the pinned cell's `CompactTable.CellContent`: the menu sits `md` from the right edge, matching the first cell's `pl-md`, and `pl-8` keeps the fade clear of the cell's content. */
export const PINNED_CELL_CONTENT_CLASS = 'pl-8 pr-md justify-end';

/** Applied to data cells' `CompactTable.CellContent`, replacing its 40px trailing padding so columns fit their content sooner. */
export const DATA_CELL_CONTENT_CLASS = 'pr-md';

/** Width of the fade at the pinned cell's leading edge, matching the cell content's `pl-8`. */
const PINNED_FADE_WIDTH = 32;
const PINNED_COLUMN_CLASS = cn('[&_td:last-child]:sticky [&_td:last-child]:right-0 [&_td:last-child]:z-1', '[&_th:last-child]:right-0 [&_th:last-child]:z-2');

/** Fades scrolled-under content into the pinned cell over its own leading 2rem, opaque on row hover, like the Markets app's sticky columns. */
const PINNED_COLUMN_FADE_CLASS = cn('[&_:is(td,th):last-child]:bg-transparent [&_:is(td,th):last-child]:bg-linear-to-r [&_:is(td,th):last-child]:from-transparent [&_:is(td,th):last-child]:to-card [&_:is(td,th):last-child]:to-[2rem]', '[&_td:last-child]:group-hover:bg-card [&_td:last-child]:group-hover:bg-none');
interface SizedColumn {
  id: string;
  getSize: () => number;
}
interface DashboardPositionsTableProps {
  /** Pass `table.getVisibleLeafColumns()`; TanStack returns a new array when visibility changes. */
  columns: SizedColumn[];
  'data-testid'?: string;
  children: ReactNode;
}

/**
 * Horizontally scrollable positions table whose position-manager column, when last, is at least its `size`
 * wide, grows to fit its content, and is pinned to the right edge on the viewer's own portfolio. Data columns
 * share the remaining width in proportion to their `size`, shrink to fit their content when space is short,
 * and the table scrolls only once the content itself no longer fits.
 */
const DashboardPositionsTable = t0 => {
  const $ = _c(29);
  const {
    columns,
    "data-testid": dataTestId,
    children
  } = t0;
  const {
    isSelf
  } = useViewedAccountAddress();
  let dataColumns;
  let dataWidth;
  let isPinned;
  let isSticky;
  let t1;
  if ($[0] !== columns || $[1] !== isSelf) {
    const lastColumn = columns.at(-1);
    const pinnedColumn = lastColumn?.id === POSITION_MANAGER_COLUMN_ID ? lastColumn : undefined;
    isPinned = pinnedColumn !== undefined;
    let t2;
    if ($[7] !== columns || $[8] !== isPinned) {
      t2 = isPinned ? columns.slice(0, -1) : columns;
      $[7] = columns;
      $[8] = isPinned;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    dataColumns = t2;
    dataWidth = dataColumns.reduce(_temp, 0);
    isSticky = isPinned && isSelf;
    t1 = pinnedColumn ? pinnedColumn.getSize() + (isSticky ? PINNED_FADE_WIDTH : 0) : 0;
    $[0] = columns;
    $[1] = isSelf;
    $[2] = dataColumns;
    $[3] = dataWidth;
    $[4] = isPinned;
    $[5] = isSticky;
    $[6] = t1;
  } else {
    dataColumns = $[2];
    dataWidth = $[3];
    isPinned = $[4];
    isSticky = $[5];
    t1 = $[6];
  }
  const pinnedWidth = t1;
  const t2 = isSticky && PINNED_COLUMN_CLASS;
  const t3 = isSticky && PINNED_COLUMN_FADE_CLASS;
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = cn("w-full table-auto", t2, t3);
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== dataColumns || $[14] !== dataWidth) {
    let t6;
    if ($[16] !== dataWidth) {
      t6 = column_0 => <col key={column_0.id} style={{
        width: `${column_0.getSize() / dataWidth * 100}%`
      }} />;
      $[16] = dataWidth;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t5 = dataColumns.map(t6);
    $[13] = dataColumns;
    $[14] = dataWidth;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[18] !== isPinned || $[19] !== pinnedWidth) {
    t6 = isPinned && <col style={{
      width: `${pinnedWidth}px`
    }} />;
    $[18] = isPinned;
    $[19] = pinnedWidth;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <colgroup>{t5}{t6}</colgroup>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== children || $[25] !== dataTestId || $[26] !== t4 || $[27] !== t7) {
    t8 = <div className="border-border overflow-x-auto rounded-b-[20px] border-t-[0.5px]"><CompactTable className={t4} data-testid={dataTestId}>{t7}{children}</CompactTable></div>;
    $[24] = children;
    $[25] = dataTestId;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
};
export default DashboardPositionsTable;
function _temp(total, column) {
  return total + column.getSize();
}